import { createFileRoute, Link } from "@tanstack/react-router";
import { PHYSICIAN_REF } from "@/lib/medical-schema";
import { Flower } from "@/components/site/Flower";
import { EditableText } from "@/components/site/EditableText";
import { PageSection } from "@/components/site/PageSection";
import { schemaDateFields } from "@/lib/schema-dates";

const SITE = "https://virginiabariatric.com";

const RESOURCE_LINKS: { title: string; to?: string; href?: string }[] = [
  { title: "Gastric Sleeve Surgery", to: "/services/sleeve" },
  { title: "Frequently Asked Questions", to: "/resources/faq" },
  { title: "Do I Qualify for Surgery?", to: "/resources/qualifications" },
  { title: "Insurance Coverage for Surgery", to: "/resources/insurance-coverage" },
  { title: "Insurance Coverage for Medication", to: "/medical-weight-loss/insurance" },
  { title: "New Patient Forms", to: "/resources/forms" },
  { title: "Pre & Post-Op Instructions", to: "/resources/instructions" },
  { title: "Nutrition & Diet Guidelines", to: "/resources/nutrition" },
  { title: "Support Groups", to: "/resources/support" },
  { title: "Webinar Schedule", href: "https://vbportal.pattrax.com/events" },
  { title: "Self-Pay Pricing", to: "/self-pay-bariatric-surgery" },
  { title: "Financing & Payment Plans", to: "/resources/prosper" },
  { title: "Suggestion Box", to: "/resources/suggestion" },
  { title: "Share Your Story", to: "/results/share-your-story" },
  { title: "Obesity and Type-2 Diabetes", to: "/resources/obesity-and-diabetes" },
  {
    title: "Weight Regain After Surgery",
    to: "/resources/managing-weight-regain-after-bariatric-surgery",
  },
];

const PAGE_TITLE = "Patient Resources & Helpful Links | Virginia Bariatric";
const PAGE_DESCRIPTION =
  "Forms, pre- and post-operative instructions, nutrition guidance, support groups, financing, and educational resources for Virginia Bariatric Surgery patients.";
const PAGE_URL = `${SITE}/resources`;
const PAGE_IMAGE = `${SITE}/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg`;
const PAGE_IMAGE_ALT =
  "Virginia Bariatric Surgery — Dr. Matthew Fitzer's Northern Virginia weight-loss practice";

export const Route = createFileRoute("/resources/")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
      { name: "description", content: PAGE_DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:site_name", content: "Virginia Bariatric Surgery" },
        { property: "og:locale", content: "en_US" },
        { property: "og:title", content: PAGE_TITLE },
        { property: "og:description", content: PAGE_DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: PAGE_URL },
        { property: "og:image", content: PAGE_IMAGE },
        { property: "og:image:secure_url", content: PAGE_IMAGE },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "1280" },
        { property: "og:image:height", content: "840" },
        { property: "og:image:alt", content: PAGE_IMAGE_ALT },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: PAGE_TITLE },
        { name: "twitter:description", content: PAGE_DESCRIPTION },
        { name: "twitter:image", content: PAGE_IMAGE },
        { name: "twitter:image:alt", content: PAGE_IMAGE_ALT },
      ] : []),
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],

    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": ["WebPage", "MedicalWebPage", "CollectionPage"],
              reviewedBy: PHYSICIAN_REF,
              "@id": `${SITE}/resources#webpage`,
              url: PAGE_URL,
              name: PAGE_TITLE,
              alternateName: ["Helpful Links", "Patient Resources"],
              headline: PAGE_TITLE,
              description: PAGE_DESCRIPTION,
              inLanguage: "en-US",
              isPartOf: { "@id": `${SITE}/#website` },
              breadcrumb: { "@id": `${SITE}/resources#breadcrumb` },
              publisher: { "@id": `${SITE}/#organization` },
              about: { "@type": "MedicalCondition", name: "Obesity" },
              audience: {
                "@type": "MedicalAudience",
                audienceType: "Bariatric surgery patients and prospective patients",
              },
              specialty: "Bariatric",
              ...schemaDateFields("/resources"),
              mainEntity: { "@id": `${SITE}/resources#resource-list` },
              significantLink: RESOURCE_LINKS.map((item) => item.href ?? `${SITE}${item.to}`),
              image: { "@id": `${SITE}/resources#primaryimage` },
              primaryImageOfPage: {
                "@type": "ImageObject",
                "@id": `${SITE}/resources#primaryimage`,
                url: PAGE_IMAGE,
                contentUrl: PAGE_IMAGE,
                width: 1280,
                height: 720,
                caption: PAGE_IMAGE_ALT,
              },

            },
            {
              "@type": "ItemList",
              "@id": `${SITE}/resources#resource-list`,
              name: "Patient Resources and Helpful Links",
              description:
                "Directory of patient resources from Virginia Bariatric Surgery, including gastric sleeve surgery information, FAQs, forms, instructions, nutrition guidance, and financing.",
              itemListOrder: "https://schema.org/ItemListOrderAscending",
              numberOfItems: RESOURCE_LINKS.length,
              itemListElement: RESOURCE_LINKS.map((item, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: item.title,
                url: item.href ?? `${SITE}${item.to}`,
              })),
            },
          ],
        }),
      },
    ] : [],
  }),
  component: ResourcesPage,
});

function ResourcesPage() {
  return (
    <>
      <PageSection className="relative pt-4 md:pt-6 pb-3 md:pb-4">
        <Flower className="hidden md:block right-2 top-6 w-56 lg:w-64" />
        <div className="text-center max-w-4xl mx-auto">
          <EditableText
            as="h1"
            contentKey="resources.hero.title"
            defaultValue="Helpful Links"
            className="text-4xl md:text-5xl font-serif"
          />
          <EditableText
            as="p"
            contentKey="resources.hero.intro"
            defaultValue="Practice forms, publications, and other resources."
            className="mt-4 md:mt-6 text-foreground/80 leading-[1.6] md:text-[1.125rem]"
          />
        </div>
      </PageSection>

      <PageSection fullWidth maxWidth="4xl" className="relative z-10 bg-brand-soft py-3 md:py-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {RESOURCE_LINKS.map((item) => (
            <ResourceCard key={item.title} title={item.title} to={item.to} href={item.href} />
          ))}
        </div>
      </PageSection>

      <PageSection className="pt-4 pb-4 text-center">
        <EditableText
          as="h2"
          contentKey="resources.cta.title"
          defaultValue="Have Questions?"
          className="text-2xl md:text-3xl font-serif"
        />
        <EditableText
          as="p"
          contentKey="resources.cta.body"
          defaultValue="Let us help you find what you need."
          className="mt-4 text-muted-foreground"
        />
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/contact"
            className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Contact Us
          </Link>
          <Link
            to="/"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Back to Homepage
          </Link>
        </div>
      </PageSection>
    </>
  );
}

function ResourceCard({
  title,
  to,
  href,
}: {
  title: string;
  to?: string;
  href?: string;
}) {
  const className =
    "group rounded-2xl border border-border bg-card py-3 px-5 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl";
  const content = (
    <>
      <h2 className="text-lg text-brand-mid">{title}</h2>
      <span className="mt-1 inline-block text-sm font-medium text-brand-mid group-hover:translate-x-1 transition">Learn More →</span>
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {content}
      </a>
    );
  }
  return (
    <Link to={to!} className={className}>
      {content}
    </Link>
  );
}
